import {loggerFactory} from "@cm/browser-log"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import type {HalPainterImageCompositor, PainterImageCompositorPaintArgs, ShaderReturnType, HalPainterPrimitiveRef} from "@cm/hal"
import type {HalGeometryRef} from "@cm/hal/hal-geometry/index"
import {Box2, Matrix3x2, Vector2} from "@cm/math"
import {WebGl2Settings} from "#webgl2/settings"

export class WebGl2PainterImageCompositor implements HalPainterImageCompositor {
    constructor(
        readonly context: WebGl2Context,
        compositingFunction?: string,
        returnType?: ShaderReturnType,
    ) {
        compositingFunction ??= DEFAULT_COMPOSITING_FUNCTION

        WebGl2PainterImageCompositor.instances?.add(this)

        returnType ??= "vec4"
        this._data = {
            painterPrimitive: this.context.createHalPainterPrimitive(this.getShadingFunction(compositingFunction, returnType), returnType),
            geometry: this.context.getUnitRectGeometry(),
        }
    }

    // HalEntity
    dispose(): void {
        if (!this._data) {
            throw new Error("WebGl2PainterImageCompositor: Already disposed")
        }
        this._data.painterPrimitive.release()
        this._data.geometry.release()
        this._data = null

        WebGl2PainterImageCompositor.instances?.delete(this)
    }

    // HalPainterImageCompositor
    paint({target, parameters, sourceImages, options}: PainterImageCompositorPaintArgs) {
        if (!this._data) {
            throw new Error("WebGl2PainterImageCompositor: Already disposed")
        }
        // const fullTargetRegion = Box2.fromPositionAndSize(new Vector2(0, 0), new Vector2(target.width, target.height))
        // const targetRegion = options?.targetRegion ? Box2.intersect(options.targetRegion, fullTargetRegion) : fullTargetRegion
        const targetRegion = options?.targetRegion ?? Box2.fromPositionAndSize(new Vector2(0, 0), new Vector2(target.width, target.height))
        const uvTransform = new Matrix3x2().translate(targetRegion).scale(new Vector2(targetRegion.width, targetRegion.height))
        const transform = options?.transform ? Matrix3x2.fromMatrix3x2Like(options.transform).append(uvTransform) : uvTransform
        const clipRegionLo = transform.multiplyVector(Vector2.zero).floorInPlace()
        const clipRegionHi = transform.multiplyVector(Vector2.one).ceilInPlace()
        const clipRegion = Box2.fromMinMax(clipRegionLo, clipRegionHi)
        if (clipRegion.isEmpty()) {
            logger.warn("WebGl2PainterImageCompositor: Clip region is empty, nothing to draw")
            return
        }
        this._data.painterPrimitive.ref.paint({
            target,
            geometry: this._data.geometry.ref,
            parameters: {
                ...parameters,
            },
            sourceImages,
            options: {
                ...options,
                transform,
                uvTransform,
                clipRegion,
            },
        })
    }

    private getShadingFunction(compositingFunction: string, returnType: ShaderReturnType): string {
        return `
            ${compositingFunction}
            
            ${returnType} computeColor(vec2 position, vec2 uv, vec4 color) {
                ivec2 texelIndex = ivec2(floor(uv));
                return computeColor(texelIndex)${returnType === "vec4" ? " * color" : ""};
            }
        `
    }

    private _data: Data | null

    static instances = WebGl2Settings.InstanceTracking ? new Set<WebGl2PainterImageCompositor>() : undefined
}

type Data = {
    painterPrimitive: HalPainterPrimitiveRef
    geometry: HalGeometryRef
}

const DEFAULT_COMPOSITING_FUNCTION = `
    vec4 computeColor(ivec2 targetPixel) {
        return texelFetch0(targetPixel);
    }
`

const logger = loggerFactory.getLogger("webgl2")
